import {
  BENEFIT_TO_MY_CHOICE_KEY,
  ContractedBenefitsSettings,
  DEFAULT_MY_CHOICE_SETTINGS,
  IBenefitDefaultSettings,
  IBenefitGroupDetails,
  IBenefitGroupSettings,
  ICreateBenefitGroupForm,
  MyChoiceSettings
} from '@epi-models/pages/BenefitGroups';
import { toDot } from './commaHelper';
import { BenefitType } from '@epi-constants/benefitTypes';
import { LunchFundsLoadingValues } from '@epi-models/pages/Home';

export type BenefitGroupTileDisplayItems = {
  individualBenefits: IBenefitGroupSettings[];
  myChoiceBenefitTypes: string[];
  myChoiceTotalBudget: number;
};

export const getBenefitGroupTileDisplayItems = (
  benefitSettings: IBenefitGroupSettings[] = [],
  contractedBenefits: ContractedBenefitsSettings[] = [],
  companyMyChoiceEnabled = false
): BenefitGroupTileDisplayItems => {
  if (!companyMyChoiceEnabled) {
    return {
      individualBenefits: benefitSettings,
      myChoiceBenefitTypes: [],
      myChoiceTotalBudget: 0
    };
  }

  const myChoiceEnabledTypes = new Set(
    contractedBenefits
      .filter(
        ({ benefitType, myChoiceEnabled }) =>
          myChoiceEnabled && benefitType.toLowerCase() !== BenefitType.Lunch
      )
      .map(({ benefitType }) => benefitType.toLowerCase())
  );

  if (myChoiceEnabledTypes.size === 0) {
    return {
      individualBenefits: benefitSettings,
      myChoiceBenefitTypes: [],
      myChoiceTotalBudget: 0
    };
  }

  const individualBenefits: IBenefitGroupSettings[] = [];
  const myChoiceBenefits: IBenefitGroupSettings[] = [];

  for (const setting of benefitSettings) {
    if (myChoiceEnabledTypes.has(setting.benefitType.toLowerCase())) {
      myChoiceBenefits.push(setting);
    } else {
      individualBenefits.push(setting);
    }
  }

  if (myChoiceBenefits.length < 2) {
    return {
      individualBenefits: [...individualBenefits, ...myChoiceBenefits],
      myChoiceBenefitTypes: [],
      myChoiceTotalBudget: 0
    };
  }

  return {
    individualBenefits,
    myChoiceBenefitTypes: myChoiceBenefits.map(
      ({ benefitType }) => benefitType
    ),
    myChoiceTotalBudget: myChoiceBenefits.reduce(
      (total, { maxAmountPerPeriod }) => total + (maxAmountPerPeriod || 0),
      0
    )
  };
};

export const formatValuesToDTO = (values: ICreateBenefitGroupForm) => {
  const formattedSettings = {};

  for (const [benefitType, benefitSettings] of Object.entries(
    values.benefitSettings
  )) {
    if (benefitSettings) {
      formattedSettings[benefitType] = {
        ...benefitSettings,
        maxAmountPerPeriod: benefitSettings?.maxAmountPerPeriod
          ? +toDot(benefitSettings?.maxAmountPerPeriod)
          : undefined
      };
    }
  }

  return {
    groupName: values.groupName,
    benefitSettings: formattedSettings
  } as ICreateBenefitGroupForm;
};

export const mapBenefitsToSettings = (
  defaultSettings?: IBenefitDefaultSettings,
  groupDetails?: IBenefitGroupDetails
) => {
  if (!defaultSettings) {
    return undefined;
  }
  const settings = {};

  for (const {
    maxAmount,
    taxFreeAmount,
    benefitType
  } of defaultSettings.contractedBenefits) {
    const existingSetting = groupDetails?.benefitSettings?.find(
      s => s.benefitType === benefitType
    );
    if (groupDetails && !existingSetting) {
      continue;
    }
    const defaultMaxAmount = taxFreeAmount ?? maxAmount;
    if (benefitType === BenefitType.Lunch) {
      settings[benefitType] = {
        maxAmountPerPeriod:
          existingSetting?.maxAmountPerPeriod ??
          defaultSettings.lunchGlobalSettings?.maxAmountPerPeriod ??
          defaultMaxAmount,
        isMaximumSpendingLimit:
          defaultSettings.lunchGlobalSettings?.isMaximumSpendingLimit ?? true,
        fundsLoadingType: defaultSettings.lunchGlobalSettings
          ?.fundsLoadingType as LunchFundsLoadingValues,
        recurringTopupRequestEnabled:
          existingSetting?.recurringTopupRequestEnabled ?? false
      };
    } else {
      settings[benefitType] = {
        maxAmountPerPeriod:
          existingSetting?.maxAmountPerPeriod ?? defaultMaxAmount
      };
    }
  }
  return settings;
};

export const mapContractedBenefitsToMyChoiceSettings = (
  contractedBenefits: ContractedBenefitsSettings[]
): MyChoiceSettings => {
  const settings = { ...DEFAULT_MY_CHOICE_SETTINGS };

  for (const benefit of contractedBenefits) {
    settings[BENEFIT_TO_MY_CHOICE_KEY[benefit.benefitType]] =
      benefit.myChoiceEnabled;
  }

  return settings;
};
